import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Check, ArrowRight, Trash2 } from 'lucide-react';

const CreateGoodsReceipt = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const poId = searchParams.get('po');

    const [loading, setLoading] = useState(true);
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);

    const [formData, setFormData] = useState({
        purchase_order_id: poId || '',
        supplier_id: '',
        reference_number: '',
        notes: '',
        items: []
    });

    // Product search
    const [productSearch, setProductSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [suppliersRes, productsRes] = await Promise.all([
                axios.get('/api/suppliers'),
                axios.get('/api/products?limit=1000')
            ]);
            setSuppliers(suppliersRes.data.suppliers || suppliersRes.data);
            setProducts(productsRes.data.products || []);

            if (poId) {
                const poRes = await axios.get(`/api/purchase-orders/${poId}`);
                const po = poRes.data.order || poRes.data;
                setFormData(prev => ({
                    ...prev,
                    supplier_id: po.supplier_id || '',
                    items: (po.items || []).map(item => ({
                        product_id: item.product_id,
                        product_name: item.product?.name || '',
                        quantity: item.quantity - (item.quantity_received || 0),
                        unit_cost: item.unit_price
                    })).filter(item => item.quantity > 0)
                }));
            }
        } catch (error) {
            toast.error('שגיאה בטעינת נתונים');
        } finally {
            setLoading(false);
        }
        };
        fetchInitialData();
    }, [poId]);

    const handleAddItem = (product) => {
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    product_id: product.id,
                    product_name: product.name,
                    quantity: 1,
                    unit_cost: product.cost_price || 0
                }
            ]
        }));
        setProductSearch('');
        setSearchResults([]);
    };

    const handleUpdateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const handleRemoveItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleSubmit = async () => {
        try {
            await axios.post('/api/goods-receipts', formData);
            toast.success('קבלת סחורה בוצעה בהצלחה');
            navigate('/admin/inventory'); // Or back to PO list
        } catch (error) {
            toast.error('שגיאה ביצירת קבלת סחורה');
        }
    };

    useEffect(() => {
        if (productSearch.length > 1) {
            const results = products.filter(p =>
                p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                p.sku?.toLowerCase().includes(productSearch.toLowerCase())
            );
            setSearchResults(results.slice(0, 10));
        } else {
            setSearchResults([]);
        }
    }, [productSearch, products]);

    if (loading) return <div className="p-8 text-center">טוען...</div>;

    return (
        <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 space-x-reverse">
                    <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">
                        <ArrowRight className="w-6 h-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">קבלת סחורה</h1>
                </div>
                <button
                    onClick={handleSubmit}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                    <Check className="w-4 h-4 ml-2" />
                    אשר קבלה
                </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ספק</label>
                        <select
                            value={formData.supplier_id}
                            onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                            disabled={!!poId}
                            className="w-full border rounded-md px-3 py-2"
                        >
                            <option value="">בחר ספק</option>
                            {suppliers.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">מספר אסמכתא (חשבונית/תעודה)</label>
                        <input
                            type="text"
                            value={formData.reference_number}
                            onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                            className="w-full border rounded-md px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">הערות</label>
                        <input
                            type="text"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full border rounded-md px-3 py-2"
                        />
                    </div>
                </div>

                <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">פריטים לקבלה</h3>

                    <div className="relative mb-4">
                        <input
                            type="text"
                            placeholder="חפש מוצר להוספה..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            className="w-full border rounded-md px-3 py-2"
                        />
                        {searchResults.length > 0 && (
                            <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                                {searchResults.map(product => (
                                    <div
                                        key={product.id}
                                        onClick={() => handleAddItem(product)}
                                        className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                                    >
                                        <span>{product.name}</span>
                                        <span className="text-gray-500 text-sm">{product.sku}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">מוצר</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-32">כמות</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-40">עלות יח'</th>
                                    <th className="px-4 py-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {formData.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 text-sm">{item.product_name}</td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value))}
                                                className="w-full border rounded px-2 py-1"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={item.unit_cost}
                                                onChange={(e) => handleUpdateItem(index, 'unit_cost', parseFloat(e.target.value))}
                                                className="w-full border rounded px-2 py-1"
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <button
                                                onClick={() => handleRemoveItem(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateGoodsReceipt;
